import React from "react";
import { Link } from "react-router-dom";
import { routes } from "../../constant";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CategoryIcon from "@mui/icons-material/Category";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const NAV_CARDS = [
  {
    key: "users",
    icon: <PeopleAltIcon sx={{ fontSize: 28 }} />,
    titleKey: "dashboard.users",
    descKey: "dashboard.usersDesc",
    route: "USERS",
    color:
      "from-blue-500/10 to-blue-600/5 border-blue-500/20 hover:border-blue-500/40",
    iconColor: "text-blue-500 dark:text-blue-400 bg-blue-500/10",
    adminOnly: true,
  },
  {
    key: "categories",
    icon: <CategoryIcon sx={{ fontSize: 28 }} />,
    titleKey: "dashboard.categories",
    descKey: "dashboard.categoriesDesc",
    route: "CATEGORIES",
    color:
      "from-purple-500/10 to-purple-600/5 border-purple-500/20 hover:border-purple-500/40",
    iconColor: "text-purple-500 dark:text-purple-400 bg-purple-500/10",
    adminOnly: true,
  },
  {
    key: "tests",
    icon: <AssignmentIcon sx={{ fontSize: 28 }} />,
    titleKey: "dashboard.tests",
    descKey: "dashboard.testsDesc",
    route: "TESTS",
    color:
      "from-green-500/10 to-green-600/5 border-green-500/20 hover:border-green-500/40",
    iconColor: "text-green-600 dark:text-green-400 bg-green-500/10",
    adminOnly: true,
  },
  {
    key: "testResult",
    icon: <BarChartIcon sx={{ fontSize: 28 }} />,
    titleKey: "dashboard.testResult",
    descKey: "dashboard.testResultDesc",
    route: "TEST_RESULTS",
    color:
      "from-orange-500/10 to-orange-600/5 border-orange-500/20 hover:border-orange-500/40",
    iconColor: "text-orange-500 dark:text-orange-400 bg-orange-500/10",
    adminOnly: true,
  },
  {
    key: "pinfl",
    icon: <FingerprintIcon sx={{ fontSize: 28 }} />,
    titleKey: "dashboard.pinfl",
    descKey: "dashboard.pinflDesc",
    route: "GENERATE_PINFL",
    color:
      "from-rose-500/10 to-rose-600/5 border-rose-500/20 hover:border-rose-500/40",
    iconColor: "text-rose-500 dark:text-rose-400 bg-rose-500/10",
    adminOnly: true,
  },
];

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);

  const visibleCards = NAV_CARDS.filter(
    (card) => !card.adminOnly || user?.isAdmin,
  );

  return (
    <section className="min-h-screen pt-24 pb-16 bg-gray-50 dark:bg-[#030712] transition-colors duration-300">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Page header */}
        <div className="mb-10 flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/25 text-[13px] font-semibold text-green-700 dark:text-green-400 mb-4">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            {t("dashboard.adminBadge")}
          </div>
          <h1 className="text-4xl font-black text-slate-800 dark:text-white tracking-tight mb-2">
            {t("dashboard.title")}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-base max-w-md">
            {t("dashboard.subtitle")}
          </p>
        </div>

        {/* Card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleCards.map((card) => (
            <Link
              key={card.key}
              to={routes.DASHBOARD[card.route]}
              className="group relative flex flex-col gap-4 p-6 rounded-2xl border bg-gradient-to-br no-underline
                bg-white dark:bg-[#0f172a]
                border-slate-200 dark:border-slate-700/50
                hover:shadow-[0_8px_32px_rgba(0,0,0,0.10)] dark:hover:shadow-[0_8px_32px_rgba(0,0,0,0.4)]
                hover:-translate-y-1 transition-all duration-200"
            >
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${card.iconColor}`}
              >
                {card.icon}
              </div>

              {/* Text */}
              <div className="flex-1">
                <h2 className="text-[17px] font-bold text-slate-800 dark:text-white mb-1">
                  {t(card.titleKey)}
                </h2>
                <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-snug">
                  {t(card.descKey) || ""}
                </p>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-between mt-1">
                {card.adminOnly && (
                  <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
                    {t("dashboard.adminOnly")}
                  </span>
                )}
                <ArrowForwardIcon
                  sx={{ fontSize: 18 }}
                  className="ml-auto text-slate-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all"
                />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
